import React from "react";
import {
  Box,
  Grid,
  MenuItem,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { SessionStepProps, PersonRef, VirtualSessionDraft } from "../types";
import PeopleField from "../components/PeopleField";
import {
  MEETING_OPTIONS,
  MEETING_SETTINGS,
  MeetingSettingField,
  SettingSection,
} from "../config/meetingSettings";
import { Toggle } from "src/UI/Toggle";

const SECTION_ORDER: SettingSection[] = [
  "GENERAL",
  "LOBBY",
  "ROLES",
  "LINK",
  "INVITE",
];

const SECTIONS_WITH_DESCRIPTION: SettingSection[] = ["LOBBY"];

const OTHERS_FIRST_SECTIONS: SettingSection[] = ["LOBBY"];

const ROLE_FILTER = "not_roles=LEARNER";

const DeliveryDetailsStep: React.FC<SessionStepProps<VirtualSessionDraft>> = ({
  data,
  setData,
  errors,
  corporateId,
  disabledFields,
  confirmedDisabledReason,
}) => {
  const { t } = useTranslation("session-wizard");
  const theme = useTheme();

  if (!data.tool) {
    return (
      <Box
        sx={{
          py: 6,
          textAlign: "center",
          fontSize: "13px",
          color: theme.palette.text.primary,
        }}
      >
        {t("details.no-tool")}
      </Box>
    );
  }

  const fields = MEETING_SETTINGS[data.tool];
  const sections = SECTION_ORDER.filter((s) =>
    fields.some((f) => f.section === s),
  );

  const patch = (key: keyof VirtualSessionDraft, value: unknown) =>
    setData({ [key]: value } as Partial<VirtualSessionDraft>);

  const renderSwitch = (field: MeetingSettingField) => {
    const disabled =
      (field.dependsOn ? !data[field.dependsOn] : false) ||
      disabledFields.has(field.key);
    const showReason =
      field.key === "confirmed" && disabled && !!confirmedDisabledReason;
    return (
      <Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            opacity: disabled ? 0.5 : 1,
          }}
        >
          <Typography
            sx={{ fontSize: "13px", color: theme.palette.text.primary }}
          >
            {t(`settings.${field.key}`)}
          </Typography>
          <Toggle
            checked={!!data[field.key]}
            disabled={disabled}
            onChange={(next) => patch(field.key, next)}
          />
        </Box>
        {showReason && (
          <Typography
            sx={{ fontSize: "11px", color: theme.palette.warning.main }}
          >
            {confirmedDisabledReason}
          </Typography>
        )}
      </Box>
    );
  };

  const renderSelect = (field: MeetingSettingField) => {
    const options = field.optionsKey
      ? MEETING_OPTIONS[field.optionsKey] || []
      : [];
    const placeholder = field.placeholderKey
      ? String(t(field.placeholderKey))
      : null;
    return (
      <TextField
        select
        fullWidth
        size="small"
        label={`${t(`settings.${field.key}`)}${field.required ? " *" : ""}`}
        value={(data[field.key] as string | null) ?? ""}
        disabled={disabledFields.has(field.key)}
        error={!!errors[field.key]}
        helperText={errors[field.key]}
        onChange={(e) => patch(field.key, e.target.value || null)}
        {...(placeholder && {
          InputLabelProps: { shrink: true },
          SelectProps: {
            displayEmpty: true,
            renderValue: (value: unknown) =>
              value ? (
                t(`options.${field.optionsKey}.${value}`)
              ) : (
                <Box
                  component="span"
                  sx={{ color: theme.palette.text.disabled }}
                >
                  {placeholder}
                </Box>
              ),
          },
        })}
      >
        {options.map((opt) => (
          <MenuItem key={opt} value={opt}>
            {t(`options.${field.optionsKey}.${opt}`)}
          </MenuItem>
        ))}
      </TextField>
    );
  };

  const renderText = (field: MeetingSettingField) => (
    <TextField
      fullWidth
      size="small"
      label={`${t(`settings.${field.key}`)}${field.required ? " *" : ""}`}
      value={(data[field.key] as string | null) ?? ""}
      disabled={disabledFields.has(field.key)}
      error={!!errors[field.key]}
      helperText={errors[field.key]}
      onChange={(e) => patch(field.key, e.target.value || null)}
    />
  );

  const renderAssociation = (field: MeetingSettingField) => (
    <PeopleField
      label={String(t(`settings.${field.key}`))}
      value={(data[field.key] as PersonRef[]) || []}
      emptyLabel={String(t("details.role-empty"))}
      pickerTitle={String(t(`settings.${field.key}`))}
      corporateId={corporateId}
      roleFilter={ROLE_FILTER}
      onChange={(people) => patch(field.key, people)}
    />
  );

  const renderSectionBody = (section: SettingSection) => {
    const sectionFields = fields.filter((f) => f.section === section);
    const switches = sectionFields.filter((f) => f.type === "switch");
    const others = sectionFields.filter((f) => f.type !== "switch");

    const switchesBlock = switches.length > 0 && (
      <Grid container spacing={1.5}>
        {switches.map((field) => (
          <Grid item xs={12} md={field.fullWidth ? 12 : 6} key={field.key}>
            {renderSwitch(field)}
          </Grid>
        ))}
      </Grid>
    );
    const othersBlock = others.map((field) => (
      <Box key={field.key}>
        {field.type === "select" && renderSelect(field)}
        {field.type === "text" && renderText(field)}
        {field.type === "association" && renderAssociation(field)}
      </Box>
    ));
    const othersFirst = OTHERS_FIRST_SECTIONS.includes(section);

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {!othersFirst && switchesBlock}
        {othersBlock}
        {othersFirst && switchesBlock}
      </Box>
    );
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {sections.map((section) => (
        <Box
          key={section}
          sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
        >
          <Typography
            sx={{
              fontSize: "15px",
              fontWeight: 700,
              color: theme.palette.text.primary,
            }}
          >
            {t(`details.sections.${section}`)}
          </Typography>
          {SECTIONS_WITH_DESCRIPTION.includes(section) && (
            <Typography
              sx={{ fontSize: "13px", color: theme.palette.text.primary }}
            >
              {t(`details.section-descriptions.${section}`)}
            </Typography>
          )}
          {renderSectionBody(section)}
        </Box>
      ))}
    </Box>
  );
};

export default DeliveryDetailsStep;
